import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import {
  IconAlertTriangle,
  IconChecks,
  IconChevronDown,
  IconChevronRight,
  IconPencil,
  IconX,
} from '@tabler/icons-react';

import type { Category } from '@interfaces/general';
import type {
  DetectedCategory,
  DetectedExtra,
  DetectedProduct,
  DetectedSection,
  ProductPresentation,
} from '@pages/Menu/Products/MenuUploaderV2/types/menuUpload.types';

import { V4 } from '../../../tokens';
import type { ConsolidatedAnalysis } from '../types';
import type { ProductDuplicateHint } from '../utils/existingCatalogMatch';

/**
 * Patron "una sola vista, cero modos" (spec UX 2026-06-10, reemplaza al modo
 * dual Seleccionar/Editar que resultó un mode-error: el mismo tap hacía cosas
 * distintas según estado invisible y la edición era indescubrible).
 *
 * Anatomía de cada fila — las dos acciones SIEMPRE disponibles, en zonas
 * táctiles separadas y con signifiers visibles:
 *
 *   [☑ zona 56px] [ cuerpo: nombre + precio ] [ ✎ 44px ]
 *
 *   - Checkbox (hit-area 56px) → incluir/excluir. Siempre visible.
 *   - Cuerpo de fila y lápiz → abren el EditSheet (edición no-destructiva,
 *     siempre cancelable). El lápiz es el signifier permanente.
 *   - Item excluido: cuerpo a opacity 0.55 (no tachado: el precio sigue legible).
 *
 * Headers de categoría llevan checkbox TRI-ESTADO (todos ✓ / algunos ▬ /
 * ninguno ▢) que hace bulk de la categoría completa; tap en el texto colapsa.
 * La toolbar ofrece tri-estado global, contador "X de Y incluidos" y orden de
 * vista (solo afecta el render, no la publicación).
 *
 * Items sin categoria (`product.category` ausente o no encontrada en categories[])
 * van a un bucket aparte arriba de las categorias normales, con CTA para
 * reasignar a una categoria existente. Si el caller no pasa `onReassignCategory`,
 * el CTA queda oculto pero el bucket sigue visible (los items sin categoria son
 * siempre un blocker para publicar).
 */
export interface ConsolidatedReviewProps {
  analysis: ConsolidatedAnalysis;
  selectedProductIds: Set<string>;
  selectedCategoryIds: Set<string>;
  selectedExtraIds: Set<string>;
  selectedSectionIds: Set<string>;
  onToggleProduct: (id: string) => void;
  onToggleCategory: (id: string) => void;
  onToggleExtra: (id: string) => void;
  onToggleSection: (id: string) => void;
  onEditProduct: (id: string, patch: { nombre?: string; precio?: number; opciones?: ProductPresentation[] }) => void;
  onEditCategory: (id: string, patch: { nombre?: string }) => void;
  onEditExtra: (id: string, patch: { nombre?: string; precio?: number }) => void;
  onEditSection: (id: string, patch: { nombre?: string }) => void;
  /**
   * V4 UX (opcional): callback para reasignar items huerfanos a una categoria
   * existente. Si el caller no lo provee, el bucket "Sin categoria" se muestra
   * pero el CTA "Asignar a..." queda oculto (los items siguen seleccionables
   * pero la reasignacion bulk requiere wiring del parent).
   *
   * Default: no-op silencioso. NO usar `undefined` para distinguir el caso:
   * la presencia de la fn deshabilita el CTA cuando productIds esta vacio o
   * cuando el modo activo es 'edit', segun la spec de UX.
   */
  onReassignCategory?: (productIds: string[], newCategoryId: string) => void;
  /**
   * Bulk global del checkbox "Todo": el padre incluye/excluye TODAS las
   * entidades (productos + categorías + extras + modificadores) de un golpe.
   * Sin él, el fallback solo togglea productos (callers legacy).
   */
  onToggleAll?: (selectAll: boolean) => void;
  /**
   * Crea una categoría sintética client-side ("+ Crear categoría nueva…" del
   * EditSheet, spec §2.4). Devuelve el id creado (para reasignar el producto)
   * o null si el nombre es inválido. Si falta, la opción no se ofrece.
   */
  onCreateCategory?: (name: string) => string | null;
  /**
   * WS-B (opcional): asociación categoría DETECTADA → categoría EXISTENTE del
   * comercio (match exacto-normalizado + overrides). Pinta el chip "se agregará a
   * [Nombre existente]" en el header de categoría; al publicar se reusa ese id.
   */
  existingCategoryLinks?: Record<string, string>;
  /** Categorías existentes ACTIVAS indexadas por id (resuelve el nombre del chip y el dropdown del editor). */
  existingCategoriesById?: Record<string, Category>;
  /** Aviso por producto "ya existe en tu menú" (detectedProductId → hint). */
  productDuplicateHints?: Record<string, ProductDuplicateHint>;
  /** Asocia (link=existingCatId) o desvincula (link=null) una categoría detectada. */
  onToggleCategoryLink?: (detectedCategoryId: string, link: string | null) => void;
}

type EditTarget =
  | { kind: 'product'; entity: DetectedProduct }
  | { kind: 'category'; entity: DetectedCategory }
  | { kind: 'extra'; entity: DetectedExtra }
  | { kind: 'section'; entity: DetectedSection };

const UNCATEGORIZED_BUCKET_ID = '__uncategorized__';

const priceFormatter = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
});

// ---------- Estilos ----------

const containerStyle: CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: 16,
  background: V4.white,
  color: V4.primary,
};

const toolbarWrapperStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  background: V4.white,
  paddingBottom: 12,
  marginBottom: 12,
  borderBottom: `1px solid ${V4.grayLight}`,
  zIndex: 5,
};

const toolbarRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};

// Zona táctil del checkbox: el visual sigue siendo 24px pero el hit-area se
// rellena a ≥48px (WCAG 2.5.8 / Fitts) y separa el toggle del tap-para-editar.
const checkboxHitZoneStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  minHeight: 48,
  flexShrink: 0,
  cursor: 'pointer',
};

// Lápiz por fila: signifier permanente de que la fila es editable.
const pencilButtonStyle: CSSProperties = {
  width: 44,
  height: 44,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: 8,
  color: V4.secondary,
  cursor: 'pointer',
  padding: 0,
};

const toolbarHintStyle: CSSProperties = {
  color: V4.grayUltraDark,
  fontSize: 12,
  marginTop: 8,
  lineHeight: 1.4,
};

const bulkActionButtonStyle: CSSProperties = {
  minHeight: 40,
  padding: '6px 12px',
  borderRadius: 6,
  border: `1px solid ${V4.grayLight}`,
  background: V4.white,
  color: V4.secondary,
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const headerStyle: CSSProperties = {
  color: V4.primary,
  fontWeight: 600,
  fontSize: 16,
  marginBottom: 12,
};

const warningBannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  background: 'rgba(254, 174, 51, 0.2)',
  border: `1px solid ${V4.warning}`,
  color: V4.primary,
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
  fontSize: 13,
  lineHeight: 1.4,
};

const categoryHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 4px',
  borderBottom: `1px solid ${V4.grayLight}`,
  cursor: 'pointer',
  userSelect: 'none',
  minHeight: 56,
};

const categoryBulkRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 4px 6px 32px',
  fontSize: 12,
  color: V4.grayUltraDark,
};

const productRowStyleBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  borderBottom: `1px solid ${V4.grayLight}`,
  minHeight: 56,
  cursor: 'pointer',
  userSelect: 'none',
};

const productRowSelectedStyle: CSSProperties = {
  ...productRowStyleBase,
  background: V4.grayUltraLight,
};

const checkboxBaseStyle: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: 6,
  border: `1.5px solid ${V4.grayDark}`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: V4.white,
};

const checkboxCheckedStyle: CSSProperties = {
  ...checkboxBaseStyle,
  background: V4.secondary,
  borderColor: V4.secondary,
};

const sectionTitleStyle: CSSProperties = {
  color: V4.primary,
  fontWeight: 600,
  fontSize: 14,
  marginTop: 24,
  marginBottom: 8,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const sectionGridStyle: CSSProperties = {
  display: 'grid',
  gap: 12,
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
};

const sectionCardStyleBase: CSSProperties = {
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 8,
  background: V4.white,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  transition: 'background 120ms ease',
  minHeight: 56,
  cursor: 'pointer',
};

const sectionCardSelectedStyle: CSSProperties = {
  ...sectionCardStyleBase,
  background: V4.grayUltraLight,
};

const sectionCardHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const sectionMetaStyle: CSSProperties = {
  color: V4.grayUltraDark,
  fontSize: 12,
  lineHeight: 1.4,
  paddingLeft: 32,
};

const sectionRequiredBadgeStyle: CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 4,
  background: V4.secondary,
  color: V4.white,
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const sectionOptionalBadgeStyle: CSSProperties = {
  ...sectionRequiredBadgeStyle,
  background: V4.grayUltraLight,
  color: V4.grayUltraDark,
};

const sectionExpandRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  background: 'transparent',
  border: 'none',
  padding: '4px 0 4px 32px',
  cursor: 'pointer',
  color: V4.secondary,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
};

const sectionOptionsListStyle: CSSProperties = {
  margin: 0,
  paddingLeft: 36,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  listStyle: 'disc inside',
  color: V4.primary,
  fontSize: 13,
};

const orphanBadgeStyle: CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 4,
  background: V4.warning,
  color: V4.primary,
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const orphanBucketHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 4px',
  borderTop: `2px solid ${V4.warning}`,
  borderBottom: `1px solid ${V4.warning}`,
  background: 'rgba(254, 174, 51, 0.1)',
  cursor: 'pointer',
  userSelect: 'none',
  minHeight: 56,
};

const reassignButtonStyle: CSSProperties = {
  marginLeft: 'auto',
  minHeight: 36,
  padding: '6px 12px',
  borderRadius: 6,
  border: `1px solid ${V4.warning}`,
  background: V4.white,
  color: V4.primary,
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const reassignButtonDisabledStyle: CSSProperties = {
  ...reassignButtonStyle,
  cursor: 'not-allowed',
  opacity: 0.5,
};

// Bottom sheet styles (modal centrado en desktop, slide-up en mobile)
const sheetBackdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(17, 26, 34, 0.55)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};

const sheetContainerStyle: CSSProperties = {
  background: V4.white,
  width: '100%',
  maxWidth: 480,
  maxHeight: '80vh',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  boxShadow: '0 -8px 24px rgba(17, 26, 34, 0.18)',
  overflowY: 'auto',
};

const sheetHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
};

const sheetTitleStyle: CSSProperties = {
  color: V4.primary,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.3,
};

const sheetMetaStyle: CSSProperties = {
  color: V4.grayUltraDark,
  fontSize: 12,
  marginTop: 4,
};

const sheetCloseButtonStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  color: V4.grayUltraDark,
  display: 'inline-flex',
};

const sheetFieldLabelStyle: CSSProperties = {
  color: V4.primary,
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 6,
  display: 'block',
};

const sheetInputStyle: CSSProperties = {
  width: '100%',
  minHeight: 44,
  padding: '10px 12px',
  borderRadius: 8,
  border: `1px solid ${V4.grayDark}`,
  fontSize: 15,
  color: V4.primary,
  background: V4.white,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  outline: 'none',
};

const sheetFooterStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
  marginTop: 8,
};

const sheetPrimaryButtonStyle: CSSProperties = {
  minHeight: 48,
  padding: '10px 20px',
  borderRadius: 8,
  border: 'none',
  background: V4.secondary,
  color: V4.white,
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const sheetGhostButtonStyle: CSSProperties = {
  minHeight: 48,
  padding: '10px 20px',
  borderRadius: 8,
  border: `1px solid ${V4.grayDark}`,
  background: V4.white,
  color: V4.primary,
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const sheetSelectStyle: CSSProperties = {
  ...sheetInputStyle,
  appearance: 'auto',
};

// ---------- Atomicos ----------

const Checkbox = ({
  checked,
  indeterminate = false,
  ariaLabel,
  onClick,
}: {
  checked: boolean;
  /** Tri-estado: "algunos hijos seleccionados". Visual de guion, aria mixed. */
  indeterminate?: boolean;
  ariaLabel: string;
  onClick?: (e: MouseEvent) => void;
}): ReactElement => (
  <span
    role="checkbox"
    aria-checked={indeterminate ? 'mixed' : checked}
    aria-label={ariaLabel}
    tabIndex={onClick ? 0 : -1}
    onClick={onClick}
    onKeyDown={(e) => {
      if (!onClick) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onClick(e as unknown as MouseEvent);
      }
    }}
    style={checked || indeterminate ? checkboxCheckedStyle : checkboxBaseStyle}
  >
    {indeterminate ? (
      <svg width="14" height="14" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2.5 6H9.5" stroke={V4.white} strokeWidth="2" strokeLinecap="round" fill="none" />
      </svg>
    ) : checked ? (
      <svg width="14" height="14" viewBox="0 0 12 12" aria-hidden="true">
        <path
          d="M2 6.5L5 9.5L10 3"
          stroke={V4.white}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    ) : null}
  </span>
);

/**
 * Checkbox envuelto en su zona táctil de 48px, deteniendo la propagación
 * para que el tap de toggle nunca abra el editor de la fila.
 *
 * @param {object} props checked/indeterminate/ariaLabel/onToggle/testId.
 * @returns {ReactElement} Zona táctil con el checkbox.
 */
const CheckboxZone = ({
  checked,
  indeterminate,
  ariaLabel,
  onToggle,
  testId,
}: {
  checked: boolean;
  indeterminate?: boolean;
  ariaLabel: string;
  onToggle: () => void;
  testId?: string;
}): ReactElement => (
  <span
    data-testid={testId}
    style={checkboxHitZoneStyle}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
  >
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      ariaLabel={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    />
  </span>
);

const formatPrice = (value: number | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'Sin precio';
  }
  return priceFormatter.format(value);
};

/**
 * Etiqueta de precio de un producto: con varias presentaciones (3 pzas / 6 pzas)
 * muestra el rango min–max para que sea evidente que hay más de un precio; con
 * una o ninguna, el precio plano.
 *
 * @param {DetectedProduct} product Producto detectado (puede traer opciones[]).
 * @returns {string} Precio formateado o rango.
 */
const productPriceLabel = (product: DetectedProduct): string => {
  const opciones = product.opciones ?? [];
  if (opciones.length > 1) {
    const precios = opciones.map((op) => op.precio).filter((p) => typeof p === 'number' && !Number.isNaN(p));
    if (precios.length > 1) {
      const min = Math.min(...precios);
      const max = Math.max(...precios);
      if (min !== max) return `${priceFormatter.format(min)} – ${priceFormatter.format(max)}`;
    }
  }
  return formatPrice(product.price);
};

/**
 * Aplica los drafts de presentaciones sobre el array original: filas inválidas
 * (nombre vacío o precio no parseable) conservan el valor original de su posición.
 *
 * @param {ProductPresentation[]} originals Presentaciones detectadas/actuales.
 * @param {Array<object>} drafts Drafts staged del sheet (presentacion + precio como strings).
 * @returns {ProductPresentation[]} Array completo con las ediciones válidas aplicadas.
 */
const buildPresentationPatch = (
  originals: ProductPresentation[],
  drafts: { presentacion: string; precio: string }[],
): ProductPresentation[] =>
  originals.map((op, idx) => {
    const draft = drafts[idx];
    if (!draft) return op;
    const draftName = draft.presentacion.trim();
    const parsedPrice = Number(draft.precio);
    const priceValid = draft.precio.trim().length > 0 && !Number.isNaN(parsedPrice) && parsedPrice >= 0;
    return {
      ...op,
      presentacion: draftName.length > 0 ? draftName : op.presentacion,
      precio: priceValid ? parsedPrice : op.precio,
    };
  });

/**
 * Construye los patches de opciones de un modificador a partir de los drafts
 * staged: solo viajan los cambios reales (nombre distinto, precio válido distinto).
 *
 * @param {Array<object>} options Opciones resueltas (id, name, price).
 * @param {object} drafts Drafts por id (name + price como strings).
 * @returns {object} Mapa extraId → patch con solo los campos que cambiaron.
 */
const buildSectionOptionPatches = (
  options: { id: string; name: string; price?: number }[],
  drafts: Record<string, { name: string; price: string }>,
): Record<string, { nombre?: string; precio?: number }> => {
  const patches: Record<string, { nombre?: string; precio?: number }> = {};
  for (const opt of options) {
    const draft = drafts[opt.id];
    if (!draft) continue;
    const optPatch: { nombre?: string; precio?: number } = {};
    const draftName = draft.name.trim();
    if (draftName.length > 0 && draftName !== opt.name) optPatch.nombre = draftName;
    if (draft.price.trim().length > 0) {
      const parsedPrice = Number(draft.price);
      if (!Number.isNaN(parsedPrice) && parsedPrice >= 0 && parsedPrice !== opt.price) {
        optPatch.precio = parsedPrice;
      }
    }
    if (Object.keys(optPatch).length > 0) patches[opt.id] = optPatch;
  }
  return patches;
};

const noopReassign = (): void => {
  // default no-op para mantener compat de props cuando el caller no
  // implementa reasignacion bulk (item seguira siendo seleccionable
  // pero el CTA "Asignar a..." quedara oculto).
};

// ---------- EditSheet ----------

/** Valor especial del select de categoría: crear una nueva (spec §2.4). */
const CREATE_CATEGORY_OPTION = '__create_category__';

/** Patch que emite el EditSheet al guardar. Campos de producto son opcionales. */
interface EditSheetPatch {
  nombre?: string;
  precio?: number;
  /** Solo productos: mover a esta categoría existente. */
  categoryId?: string;
  /** Solo productos: crear una categoría nueva con este nombre y mover ahí. */
  createCategoryName?: string;
  /** Solo productos: nuevo estado del switch "Incluir en mi menú" (solo si cambió). */
  included?: boolean;
  /** Solo modificadores: patches de sus opciones (extras) editadas inline. */
  optionPatches?: Record<string, { nombre?: string; precio?: number }>;
  /** Solo productos multipresentación: el array COMPLETO de presentaciones editadas. */
  productOptions?: ProductPresentation[];
}

interface EditSheetProps {
  target: EditTarget;
  categories: DetectedCategory[];
  /** Estado actual de inclusión (switch). Undefined cuando el target no es producto. */
  included?: boolean;
  /** Muestra el select de categoría (productos) cuando el padre soporta reasignación. */
  allowCategoryChange?: boolean;
  /** Habilita la opción "+ Crear categoría nueva…" dentro del select. */
  allowCreateCategory?: boolean;
  onClose: () => void;
  onSubmit: (patch: EditSheetPatch) => void;
  /**
   * Opciones del modificador (extras resueltos) cuando el target es una
   * sección: cada una se edita inline (nombre + precio) y los cambios viajan
   * en `patch.optionPatches` al Guardar.
   */
  sectionOptions?: { id: string; name: string; price?: number }[];
}

const includeRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  paddingTop: 12,
  borderTop: `1px solid ${V4.grayLight}`,
  minHeight: 44,
};

const switchTrackStyle: CSSProperties = {
  width: 40,
  height: 22,
  borderRadius: 9999,
  border: 'none',
  padding: 2,
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'background 200ms ease',
  flexShrink: 0,
};

const switchKnobStyle: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: V4.white,
  display: 'block',
  transition: 'transform 200ms ease',
};

type PresentationDraft = { presentacion: string; precio: string };

const SHEET_TITLES: Record<EditTarget['kind'], string> = {
  product: 'Editar producto',
  category: 'Editar categoria',
  extra: 'Editar extra',
  section: 'Editar modificador',
};

/**
 * Filas editables de presentaciones del producto (nombre + precio por fila).
 *
 * @param {object} props presentations originales, drafts staged y setter.
 * @returns {ReactElement} Bloque de inputs por presentación.
 */
const PresentationRows = ({
  presentations,
  drafts,
  onChange,
}: {
  presentations: ProductPresentation[];
  drafts: PresentationDraft[];
  onChange: (updater: (prev: PresentationDraft[]) => PresentationDraft[]) => void;
}): ReactElement => (
  <div>
    <span style={sheetFieldLabelStyle}>Presentaciones (nombre y precio)</span>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {presentations.map((op, idx) => (
        <div key={`${op.presentacion}-${idx}`} style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            aria-label={`Nombre de la presentación ${op.presentacion}`}
            data-testid={`consolidated-review-edit-sheet-presentation-${idx}-name`}
            value={drafts[idx]?.presentacion ?? ''}
            onChange={(e) =>
              onChange((prev) => prev.map((d, i) => (i === idx ? { ...d, presentacion: e.target.value } : d)))
            }
            style={{ ...sheetInputStyle, flex: 2 }}
          />
          <input
            type="number"
            step="0.01"
            min="0"
            aria-label={`Precio de la presentación ${op.presentacion}`}
            data-testid={`consolidated-review-edit-sheet-presentation-${idx}-price`}
            value={drafts[idx]?.precio ?? ''}
            onChange={(e) => onChange((prev) => prev.map((d, i) => (i === idx ? { ...d, precio: e.target.value } : d)))}
            style={{ ...sheetInputStyle, flex: 1 }}
          />
        </div>
      ))}
    </div>
    <div style={{ fontSize: 11, color: V4.grayUltraDark, marginTop: 6 }}>
      Cada presentación se publica con su propio precio (ej. 3 pzas / 6 pzas).
    </div>
  </div>
);

const EditSheet = ({
  target,
  categories,
  included,
  allowCategoryChange = false,
  allowCreateCategory = false,
  onClose,
  onSubmit,
  sectionOptions,
}: EditSheetProps): ReactElement => {
  const initialName = target.entity.name;
  const isProduct = target.kind === 'product';
  // Producto multipresentación (3 pzas / 6 pzas): se editan TODAS las
  // presentaciones en lugar del precio plano.
  const productPresentations = isProduct ? (target.entity.opciones ?? []) : [];
  const isMultiPresentation = productPresentations.length > 1;
  const hasPrice = (target.kind === 'product' && !isMultiPresentation) || target.kind === 'extra';
  const initialPrice =
    target.kind === 'product' ? target.entity.price : target.kind === 'extra' ? target.entity.price : undefined;
  const [name, setName] = useState<string>(initialName);
  const [priceDraft, setPriceDraft] = useState<string>(
    typeof initialPrice === 'number' && !Number.isNaN(initialPrice) ? String(initialPrice) : '',
  );
  // Drafts staged de presentaciones (nombre de presentación + precio).
  const [presDrafts, setPresDrafts] = useState<{ presentacion: string; precio: string }[]>(() =>
    productPresentations.map((op) => ({
      presentacion: op.presentacion,
      precio: typeof op.precio === 'number' && !Number.isNaN(op.precio) ? String(op.precio) : '',
    })),
  );
  // Select de categoría (solo productos): el valor inicial es la categoría
  // actual SI es conocida; un huérfano arranca en '' ("Sin categoría").
  const rawInitialCategory = isProduct ? (target.entity.category ?? '') : '';
  const initialCategoryId = categories.some((c) => c.id === rawInitialCategory) ? rawInitialCategory : '';
  const [categoryDraft, setCategoryDraft] = useState<string>(initialCategoryId);
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  // Switch "Incluir en mi menú" staged: se aplica al Guardar (cancelar no toca nada).
  const [includedDraft, setIncludedDraft] = useState<boolean>(included ?? true);
  // Opciones del modificador (staged): nombre y precio editables por opción.
  // El precio vive en el EXTRA referenciado (el modificador no tiene precio propio).
  const [optionDrafts, setOptionDrafts] = useState<Record<string, { name: string; price: string }>>(() =>
    Object.fromEntries(
      (sectionOptions ?? []).map((opt) => [
        opt.id,
        { name: opt.name, price: typeof opt.price === 'number' && !Number.isNaN(opt.price) ? String(opt.price) : '' },
      ]),
    ),
  );
  const creatingCategory = categoryDraft === CREATE_CATEGORY_OPTION;
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    nameInputRef.current?.focus();
    nameInputRef.current?.select();
  }, []);

  const contextLine = useMemo(() => {
    if (target.kind === 'product') {
      const catId = target.entity.category;
      const cat = catId ? categories.find((c) => c.id === catId) : undefined;
      return cat ? `Categoria: ${cat.name}` : 'Sin categoria asignada';
    }
    if (target.kind === 'category') return 'Categoria';
    if (target.kind === 'extra') return 'Extra (modificador suelto)';
    return `Modificador · ${target.entity.opciones.length} opcion(es)`;
  }, [target, categories]);

  const submitDisabled = name.trim().length === 0 || (creatingCategory && newCategoryName.trim().length === 0);

  const handleSubmit = (): void => {
    if (submitDisabled) return;
    const trimmed = name.trim();
    const patch: EditSheetPatch = { nombre: trimmed };
    if (hasPrice && priceDraft.trim().length > 0) {
      const parsed = Number(priceDraft);
      if (!Number.isNaN(parsed) && parsed >= 0) patch.precio = parsed;
    }
    if (isProduct) {
      if (creatingCategory) {
        patch.createCategoryName = newCategoryName.trim();
      } else if (allowCategoryChange && categoryDraft && categoryDraft !== initialCategoryId) {
        patch.categoryId = categoryDraft;
      }
      if (included !== undefined && includedDraft !== included) {
        patch.included = includedDraft;
      }
      if (isMultiPresentation) {
        patch.productOptions = buildPresentationPatch(productPresentations, presDrafts);
      }
    }
    if (sectionOptions && sectionOptions.length > 0) {
      const optionPatches = buildSectionOptionPatches(sectionOptions, optionDrafts);
      if (Object.keys(optionPatches).length > 0) patch.optionPatches = optionPatches;
    }
    onSubmit(patch);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const title = SHEET_TITLES[target.kind];

  return (
    <div
      data-testid="consolidated-review-edit-sheet"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={sheetBackdropStyle}
      onClick={onClose}
    >
      <div style={sheetContainerStyle} onClick={(e) => e.stopPropagation()}>
        <div style={sheetHeaderStyle}>
          <div>
            <div style={sheetTitleStyle}>{title}</div>
            <div style={sheetMetaStyle}>{contextLine}</div>
          </div>
          <button
            type="button"
            aria-label="Cerrar editor"
            data-testid="consolidated-review-edit-sheet-close"
            style={sheetCloseButtonStyle}
            onClick={onClose}
          >
            <IconX size={20} />
          </button>
        </div>

        <div>
          <label htmlFor="edit-sheet-name" style={sheetFieldLabelStyle}>
            Nombre
          </label>
          <input
            id="edit-sheet-name"
            ref={nameInputRef}
            type="text"
            data-testid="consolidated-review-edit-sheet-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            style={sheetInputStyle}
          />
        </div>

        {hasPrice ? (
          <div>
            <label htmlFor="edit-sheet-price" style={sheetFieldLabelStyle}>
              Precio (MXN)
            </label>
            <input
              id="edit-sheet-price"
              type="number"
              step="0.01"
              min="0"
              data-testid="consolidated-review-edit-sheet-price"
              value={priceDraft}
              onChange={(e) => setPriceDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              style={sheetInputStyle}
            />
          </div>
        ) : null}

        {isMultiPresentation ? (
          <PresentationRows presentations={productPresentations} drafts={presDrafts} onChange={setPresDrafts} />
        ) : null}

        {isProduct && allowCategoryChange ? (
          <div>
            <label htmlFor="edit-sheet-category" style={sheetFieldLabelStyle}>
              Categoría
            </label>
            <select
              id="edit-sheet-category"
              data-testid="consolidated-review-edit-sheet-category"
              value={categoryDraft}
              onChange={(e) => setCategoryDraft(e.target.value)}
              style={sheetSelectStyle}
            >
              {initialCategoryId === '' ? <option value="">Sin categoría</option> : null}
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              {allowCreateCategory ? <option value={CREATE_CATEGORY_OPTION}>+ Crear categoría nueva…</option> : null}
            </select>
            {creatingCategory ? (
              <input
                type="text"
                data-testid="consolidated-review-edit-sheet-new-category"
                aria-label="Nombre de la categoría nueva"
                placeholder="Nombre de la categoría nueva"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{ ...sheetInputStyle, marginTop: 8 }}
              />
            ) : null}
          </div>
        ) : null}

        {sectionOptions && sectionOptions.length > 0 ? (
          <div>
            <span style={sheetFieldLabelStyle}>Opciones (nombre y precio)</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sectionOptions.map((opt) => (
                <div key={opt.id} style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    aria-label={`Nombre de la opción ${opt.name}`}
                    data-testid={`consolidated-review-edit-sheet-option-${opt.id}-name`}
                    value={optionDrafts[opt.id]?.name ?? ''}
                    onChange={(e) =>
                      setOptionDrafts((prev) => ({
                        ...prev,
                        [opt.id]: { name: e.target.value, price: prev[opt.id]?.price ?? '' },
                      }))
                    }
                    style={{ ...sheetInputStyle, flex: 2 }}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="$0"
                    aria-label={`Precio de la opción ${opt.name}`}
                    data-testid={`consolidated-review-edit-sheet-option-${opt.id}-price`}
                    value={optionDrafts[opt.id]?.price ?? ''}
                    onChange={(e) =>
                      setOptionDrafts((prev) => ({
                        ...prev,
                        [opt.id]: { name: prev[opt.id]?.name ?? '', price: e.target.value },
                      }))
                    }
                    style={{ ...sheetInputStyle, flex: 1 }}
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: V4.grayUltraDark, marginTop: 6 }}>
              El precio es de cada opción ($0 = sin costo). El modificador no tiene precio propio.
            </div>
          </div>
        ) : null}

        {isProduct && included !== undefined ? (
          <div style={includeRowStyle}>
            <span style={{ fontSize: 14, fontWeight: 600, color: V4.primary }}>Incluir en mi menú</span>
            <button
              type="button"
              role="switch"
              aria-checked={includedDraft}
              aria-label="Incluir en mi menú"
              data-testid="consolidated-review-edit-sheet-include"
              style={{ ...switchTrackStyle, background: includedDraft ? V4.secondary : V4.grayDark }}
              onClick={() => setIncludedDraft((v) => !v)}
            >
              <span style={{ ...switchKnobStyle, transform: includedDraft ? 'translateX(18px)' : 'translateX(0)' }} />
            </button>
          </div>
        ) : null}

        <div style={{ fontSize: 12, color: V4.grayUltraDark }}>Podrás cambiar todo después desde tu panel.</div>

        <div style={sheetFooterStyle}>
          <button
            type="button"
            data-testid="consolidated-review-edit-sheet-cancel"
            style={sheetGhostButtonStyle}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="consolidated-review-edit-sheet-submit"
            style={sheetPrimaryButtonStyle}
            disabled={submitDisabled}
            onClick={handleSubmit}
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

export const ConsolidatedReview = ({
  analysis,
  selectedProductIds,
  selectedCategoryIds,
  selectedExtraIds,
  selectedSectionIds,
  onToggleProduct,
  onToggleCategory,
  onToggleExtra,
  onToggleSection,
  onToggleAll,
  onEditProduct,
  onEditCategory,
  onEditExtra,
  onEditSection,
  onReassignCategory = noopReassign,
  onCreateCategory,
  existingCategoryLinks,
  existingCategoriesById,
  productDuplicateHints,
  onToggleCategoryLink,
}: ConsolidatedReviewProps): ReactElement => {
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [orphanCollapsed, setOrphanCollapsed] = useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [reassignOpen, setReassignOpen] = useState<boolean>(false);
  const [reassignTargetId, setReassignTargetId] = useState<string>('');

  // Si el caller no provee callback de reasignacion, ocultamos el CTA.
  const canReassign = onReassignCategory !== noopReassign;

  // ---------- Index ----------

  const knownCategoryIds = useMemo(() => {
    const set = new Set<string>();
    analysis.categories.forEach((c) => set.add(c.id));
    return set;
  }, [analysis.categories]);

  // WS-B: categorías existentes activas (para el control "Asociar a existente" de
  // las categorías que NO auto-matchearon). Vacío en onboarding → no se ofrece.
  const existingCategoryOptions = useMemo(
    () => (existingCategoriesById ? Object.values(existingCategoriesById) : []),
    [existingCategoriesById],
  );

  const productsByCategory = useMemo(() => {
    const map = new Map<string, DetectedProduct[]>();
    analysis.categories.forEach((c) => map.set(c.id, []));
    map.set(UNCATEGORIZED_BUCKET_ID, []);
    analysis.products.forEach((product) => {
      const key =
        product.category && knownCategoryIds.has(product.category) ? product.category : UNCATEGORIZED_BUCKET_ID;
      const bucket = map.get(key) ?? [];
      bucket.push(product);
      map.set(key, bucket);
    });
    return map;
  }, [analysis.products, analysis.categories, knownCategoryIds]);

  const orphans = useMemo(() => productsByCategory.get(UNCATEGORIZED_BUCKET_ID) ?? [], [productsByCategory]);

  const extrasById = useMemo(() => {
    const map = new Map<string, DetectedExtra>();
    analysis.extras.forEach((extra) => map.set(extra.id, extra));
    return map;
  }, [analysis.extras]);

  const totalProducts = analysis.products.length;
  const totalCategories = analysis.categories.length;
  const totalSelectedProducts = useMemo(
    () => analysis.products.filter((p) => selectedProductIds.has(p.id)).length,
    [analysis.products, selectedProductIds],
  );

  // ---------- Bulk select handlers ----------

  const toggleAllGlobal = useCallback((): void => {
    // Si TODO esta seleccionado, deseleccionar todo. Si no, seleccionar todo.
    const allSelected = totalSelectedProducts === totalProducts && totalProducts > 0;
    // "Todo" significa TODO: el bulk delegado al padre cubre también
    // categorías, extras y modificadores. Un "seleccionar todo" que solo
    // marcaba productos dejaba estados inconsistentes (productos sin su
    // categoría) que degeneraban en publicaciones parciales.
    if (onToggleAll) {
      onToggleAll(!allSelected);
      return;
    }
    analysis.products.forEach((p) => {
      const isSelected = selectedProductIds.has(p.id);
      if (allSelected && isSelected) onToggleProduct(p.id);
      else if (!allSelected && !isSelected) onToggleProduct(p.id);
    });
  }, [analysis.products, onToggleAll, onToggleProduct, selectedProductIds, totalProducts, totalSelectedProducts]);

  // ---------- Edit sheet handlers ----------

  const openEdit = useCallback((target: EditTarget): void => {
    setEditTarget(target);
  }, []);

  const closeSheet = useCallback((): void => {
    setEditTarget(null);
  }, []);

  // ESC en desktop cancela el sheet de edicion. Swipe-down en mobile se
  // emula con tap en el backdrop (UX equivalente sin gesture lib).
  useEffect(() => {
    if (!editTarget && !reassignOpen) return undefined;
    const handler = (e: Event): void => {
      const key = (e as unknown as { key?: string }).key;
      if (key === 'Escape') {
        if (editTarget) closeSheet();
        if (reassignOpen) setReassignOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editTarget, reassignOpen, closeSheet]);

  // ---------- Row handlers (sin modos: tap en cuerpo = editar, checkbox = toggle) ----------

  const toggleCategoryCollapsed = (categoryId: string): void => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const toggleSectionExpanded = (sectionId: string): void => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  // ---------- Renderers ----------

  const renderProduct = (product: DetectedProduct): ReactElement => {
    const checked = selectedProductIds.has(product.id);
    const openProductEdit = (): void => openEdit({ kind: 'product', entity: product });
    // WS-B: aviso suave "ya existe en tu menú" (no bloquea ni deselecciona).
    const dupHint = productDuplicateHints?.[product.id];
    return (
      <div
        key={product.id}
        data-testid={`consolidated-review-product-${product.id}`}
        data-selected={checked}
        role="button"
        tabIndex={0}
        aria-label={`Editar producto ${product.name}`}
        style={{ ...(checked ? productRowSelectedStyle : productRowStyleBase), padding: '4px 8px 4px 0' }}
        onClick={openProductEdit}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            openProductEdit();
          }
        }}
      >
        <CheckboxZone
          checked={checked}
          ariaLabel={`Incluir producto ${product.name}`}
          onToggle={() => onToggleProduct(product.id)}
          testId={`consolidated-review-product-${product.id}-check`}
        />
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: V4.primary,
            opacity: checked ? 1 : 0.55,
          }}
        >
          {product.name}
        </span>
        {dupHint ? (
          <span
            data-testid={`consolidated-review-product-${product.id}-dup`}
            title={
              dupHint.existingPrice !== null
                ? `Ya tienes "${dupHint.existingProductName}" ($${dupHint.existingPrice}) en tu menú`
                : `Ya tienes "${dupHint.existingProductName}" en tu menú`
            }
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: V4.grayUltraDark,
              background: V4.grayUltraLight,
              padding: '1px 6px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              opacity: checked ? 1 : 0.55,
            }}
          >
            ya existe
            {dupHint.existingPrice !== null && !dupHint.priceClose ? ` · $${dupHint.existingPrice}` : ''}
          </span>
        ) : null}
        <span style={{ color: V4.primary, fontSize: 14, opacity: checked ? 1 : 0.55 }}>
          {productPriceLabel(product)}
        </span>
        <button
          type="button"
          data-testid={`consolidated-review-product-${product.id}-edit`}
          aria-label={`Editar ${product.name}`}
          style={pencilButtonStyle}
          onClick={(e) => {
            e.stopPropagation();
            openProductEdit();
          }}
        >
          <IconPencil size={18} />
        </button>
      </div>
    );
  };

  const renderCategory = (category: DetectedCategory): ReactElement => {
    const checked = selectedCategoryIds.has(category.id);
    const collapsed = collapsedCategories.has(category.id);
    const products = productsByCategory.get(category.id) ?? [];
    const ChevronIcon = collapsed ? IconChevronRight : IconChevronDown;
    const selectedCount = products.filter((p) => selectedProductIds.has(p.id)).length;
    const allChildrenSelected = products.length > 0 && selectedCount === products.length;
    const someChildrenSelected = selectedCount > 0 && !allChildrenSelected;
    // Tri-estado: checked si la categoría + todos sus hijos están dentro;
    // indeterminate si hay mezcla. El click delega al cascade simétrico del padre.
    const triChecked = checked && (products.length === 0 || allChildrenSelected);
    // WS-B: ¿esta categoría detectada se asoció a una existente del comercio?
    // (linkedExisting undefined si el id es stale/inactivo → no se pinta el chip).
    const linkedExistingId = existingCategoryLinks?.[category.id];
    const linkedExisting = linkedExistingId ? existingCategoriesById?.[linkedExistingId] : undefined;

    return (
      <div key={category.id} data-testid={`consolidated-review-category-${category.id}`}>
        <div
          style={{ ...categoryHeaderStyle, padding: '4px 8px 4px 0' }}
          role="button"
          tabIndex={0}
          aria-label={`Categoria ${category.name}: ${collapsed ? 'expandir' : 'colapsar'}`}
          aria-expanded={!collapsed}
          onClick={() => toggleCategoryCollapsed(category.id)}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              toggleCategoryCollapsed(category.id);
            }
          }}
        >
          <CheckboxZone
            checked={triChecked}
            indeterminate={someChildrenSelected}
            ariaLabel={`Incluir categoria ${category.name} completa`}
            onToggle={() => onToggleCategory(category.id)}
            testId={`consolidated-review-category-${category.id}-check`}
          />
          <ChevronIcon size={16} color={V4.grayUltraDark} />
          <span style={{ flex: 1, fontWeight: 600, color: V4.primary }}>{category.name}</span>
          <span style={{ color: V4.grayUltraDark, fontSize: 13 }}>
            {selectedCount}/{products.length}
          </span>
          <button
            type="button"
            data-testid={`consolidated-review-category-${category.id}-edit`}
            aria-label={`Editar categoria ${category.name}`}
            style={pencilButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              openEdit({ kind: 'category', entity: category });
            }}
          >
            <IconPencil size={18} />
          </button>
        </div>
        {linkedExisting ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              padding: '0 8px 6px 56px',
            }}
          >
            <span
              data-testid={`consolidated-review-category-${category.id}-link`}
              aria-label={`Esta categoría se agregará a la categoría existente ${linkedExisting.nombre}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: V4.grayUltraLight,
                color: V4.secondaryDark,
                padding: '2px 8px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              ✓ Se agregará a «{linkedExisting.nombre}»
            </span>
            {onToggleCategoryLink ? (
              <button
                type="button"
                data-testid={`consolidated-review-category-${category.id}-unlink`}
                onClick={() => onToggleCategoryLink(category.id, null)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: 12,
                  color: V4.grayUltraDark,
                  textDecoration: 'underline',
                }}
              >
                Desvincular
              </button>
            ) : null}
          </div>
        ) : onToggleCategoryLink && existingCategoryOptions.length > 0 ? (
          // Categoría detectada que NO auto-matcheó: permite asociarla a mano a una
          // existente del comercio (R2). La asociación es a nivel categoría — los
          // productos la siguen vía el seam del publish.
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '0 8px 6px 56px' }}>
            <select
              data-testid={`consolidated-review-category-${category.id}-associate`}
              defaultValue=""
              aria-label={`Asociar la categoría ${category.name} a una existente de tu menú`}
              onChange={(e) => {
                if (e.target.value) onToggleCategoryLink(category.id, e.target.value);
              }}
              style={{
                fontSize: 12,
                color: V4.grayUltraDark,
                border: `1px solid ${V4.grayLight}`,
                borderRadius: 6,
                padding: '2px 6px',
              }}
            >
              <option value="">Asociar a categoría existente…</option>
              {existingCategoryOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {!collapsed && products.length > 0 ? <div>{products.map((p) => renderProduct(p))}</div> : null}
      </div>
    );
  };

  const renderOrphanBucket = (): ReactElement | null => {
    if (orphans.length === 0) return null;
    const selectedOrphans = orphans.filter((p) => selectedProductIds.has(p.id));
    const selectedOrphanIds = selectedOrphans.map((p) => p.id);
    const allOrphansSelected = orphans.length > 0 && selectedOrphans.length === orphans.length;
    const showReassignCTA = canReassign;
    const reassignDisabled = selectedOrphanIds.length === 0;

    return (
      <div data-testid="consolidated-review-orphan-bucket">
        <div
          style={orphanBucketHeaderStyle}
          role="button"
          tabIndex={0}
          aria-label="Bucket sin categoria asignada"
          aria-expanded={!orphanCollapsed}
          title="Estos productos no tienen categoria — debes asignarlos para poder publicarlos"
          onClick={() => setOrphanCollapsed((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              setOrphanCollapsed((v) => !v);
            }
          }}
        >
          <IconAlertTriangle size={18} color={V4.warningDark} />
          <span style={{ fontWeight: 600, color: V4.primary }}>
            Sin categoria asignada
            <span style={orphanBadgeStyle}>{orphans.length}</span>
          </span>
          {showReassignCTA ? (
            <button
              type="button"
              data-testid="consolidated-review-orphan-reassign"
              aria-label="Asignar productos sin categoria a una existente"
              disabled={reassignDisabled}
              style={reassignDisabled ? reassignButtonDisabledStyle : reassignButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                if (reassignDisabled) return;
                setReassignTargetId(analysis.categories[0]?.id ?? '');
                setReassignOpen(true);
              }}
            >
              Asignar a...
            </button>
          ) : null}
          {(orphanCollapsed ? IconChevronRight : IconChevronDown)({ size: 16, color: V4.grayUltraDark })}
        </div>
        {!orphanCollapsed ? (
          <>
            {orphans.length > 0 ? (
              <div style={categoryBulkRowStyle}>
                <button
                  type="button"
                  data-testid="consolidated-review-orphan-bulk-toggle"
                  style={bulkActionButtonStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    orphans.forEach((p) => {
                      const isSelected = selectedProductIds.has(p.id);
                      if (allOrphansSelected && isSelected) onToggleProduct(p.id);
                      else if (!allOrphansSelected && !isSelected) onToggleProduct(p.id);
                    });
                  }}
                >
                  <IconChecks size={14} />
                  {allOrphansSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </button>
                <span>
                  {selectedOrphans.length}/{orphans.length} seleccionados
                </span>
              </div>
            ) : null}
            <div>{orphans.map((p) => renderProduct(p))}</div>
          </>
        ) : null}
      </div>
    );
  };

  const renderExtra = (extra: DetectedExtra): ReactElement => {
    const checked = selectedExtraIds.has(extra.id);
    const openExtraEdit = (): void => openEdit({ kind: 'extra', entity: extra });
    return (
      <div
        key={extra.id}
        data-testid={`consolidated-review-extra-${extra.id}`}
        data-selected={checked}
        role="button"
        tabIndex={0}
        aria-label={`Editar extra ${extra.name}`}
        style={{ ...(checked ? productRowSelectedStyle : productRowStyleBase), padding: '4px 8px 4px 0' }}
        onClick={openExtraEdit}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            openExtraEdit();
          }
        }}
      >
        <CheckboxZone
          checked={checked}
          ariaLabel={`Incluir extra ${extra.name}`}
          onToggle={() => onToggleExtra(extra.id)}
          testId={`consolidated-review-extra-${extra.id}-check`}
        />
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: V4.primary,
            opacity: checked ? 1 : 0.55,
          }}
        >
          {extra.name}
        </span>
        <span style={{ color: V4.grayUltraDark, fontSize: 13, opacity: checked ? 1 : 0.55 }}>
          {formatPrice(extra.price)}
        </span>
        <button
          type="button"
          data-testid={`consolidated-review-extra-${extra.id}-edit`}
          aria-label={`Editar ${extra.name}`}
          style={pencilButtonStyle}
          onClick={(e) => {
            e.stopPropagation();
            openExtraEdit();
          }}
        >
          <IconPencil size={18} />
        </button>
      </div>
    );
  };

  const renderSection = (section: DetectedSection): ReactElement => {
    const checked = selectedSectionIds.has(section.id);
    const expanded = expandedSections.has(section.id);
    const ChevronIcon = expanded ? IconChevronDown : IconChevronRight;
    const allowsMultiple = section.es_multiplo === 'multiple';
    const choiceLabel = allowsMultiple ? `puede elegir varias (hasta ${section.numero_de_opciones})` : 'elige 1';
    const requiredLabel = section.obligatorio ? 'obligatorio' : 'opcional';
    const totalOptions = section.opciones.length;
    const optionsCountLabel = `${totalOptions} opcion${totalOptions === 1 ? '' : 'es'}`;
    const cardStyle = checked ? sectionCardSelectedStyle : sectionCardStyleBase;
    const openSectionEdit = (): void => openEdit({ kind: 'section', entity: section });

    return (
      <div
        key={section.id}
        data-testid={`consolidated-review-section-${section.id}`}
        data-selected={checked}
        style={cardStyle}
        role="button"
        tabIndex={0}
        aria-label={`Editar modificador ${section.name}`}
        onClick={openSectionEdit}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            openSectionEdit();
          }
        }}
      >
        <div style={sectionCardHeaderStyle}>
          <CheckboxZone
            checked={checked}
            ariaLabel={`Incluir modificador ${section.name}`}
            onToggle={() => onToggleSection(section.id)}
            testId={`consolidated-review-section-${section.id}-check`}
          />
          <button
            type="button"
            data-testid={`consolidated-review-section-${section.id}-edit`}
            aria-label={`Editar ${section.name}`}
            style={{ ...pencilButtonStyle, order: 99 }}
            onClick={(e) => {
              e.stopPropagation();
              openSectionEdit();
            }}
          >
            <IconPencil size={18} />
          </button>
          <span
            style={{
              flex: 1,
              color: V4.primary,
              fontSize: 14,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {section.name}
            {section.obligatorio ? (
              <span style={sectionRequiredBadgeStyle}>Obligatorio</span>
            ) : (
              <span style={sectionOptionalBadgeStyle}>Opcional</span>
            )}
          </span>
        </div>

        <div style={sectionMetaStyle}>
          {optionsCountLabel} · {choiceLabel} · {requiredLabel}
        </div>

        {totalOptions > 0 ? (
          <button
            type="button"
            data-testid={`consolidated-review-section-${section.id}-expand`}
            style={sectionExpandRowStyle}
            aria-expanded={expanded}
            onClick={(e) => {
              e.stopPropagation();
              toggleSectionExpanded(section.id);
            }}
          >
            <ChevronIcon size={14} />
            {expanded ? 'Ocultar opciones' : 'Ver opciones'}
          </button>
        ) : null}

        {expanded && totalOptions > 0 ? (
          <ul data-testid={`consolidated-review-section-${section.id}-options`} style={sectionOptionsListStyle}>
            {section.opciones.map((extId) => {
              const extra = extrasById.get(extId);
              if (extra) {
                return (
                  <li key={extId} data-testid={`consolidated-review-section-${section.id}-option-${extId}`}>
                    {extra.name}
                  </li>
                );
              }
              return (
                <li key={extId} data-testid={`consolidated-review-section-${section.id}-option-${extId}-missing`}>
                  <span style={{ color: V4.grayUltraDark }}>{extId}</span>
                  <span style={orphanBadgeStyle}>no encontrado</span>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    );
  };

  // ---------- Bottom sheet (edit) ----------

  const renderEditSheet = (): ReactElement | null => {
    if (!editTarget) return null;

    // Opciones del modificador resueltas a extras (nombre + precio editables).
    const sectionOptions =
      editTarget.kind === 'section'
        ? editTarget.entity.opciones
            .map((extId) => extrasById.get(extId))
            .filter((e): e is DetectedExtra => Boolean(e))
            .map((e) => ({ id: e.id, name: e.name, price: e.price }))
        : undefined;

    return (
      <EditSheet
        target={editTarget}
        categories={analysis.categories}
        included={editTarget.kind === 'product' ? selectedProductIds.has(editTarget.entity.id) : undefined}
        allowCategoryChange={canReassign}
        allowCreateCategory={Boolean(onCreateCategory)}
        onClose={closeSheet}
        sectionOptions={sectionOptions}
        onSubmit={(patch) => {
          if (editTarget.kind === 'product') {
            const productId = editTarget.entity.id;
            onEditProduct(productId, { nombre: patch.nombre, precio: patch.precio, opciones: patch.productOptions });
            // Reasignación de categoría: la creación sintética tiene prioridad
            // (el select quedó en "+ Crear categoría nueva…" con nombre tecleado).
            let targetCategoryId = patch.categoryId;
            if (patch.createCategoryName && onCreateCategory) {
              const createdId = onCreateCategory(patch.createCategoryName);
              if (createdId) targetCategoryId = createdId;
            }
            if (targetCategoryId && canReassign) {
              onReassignCategory([productId], targetCategoryId);
            }
            // El switch sólo viene en el patch cuando CAMBIÓ: un toggle basta.
            if (patch.included !== undefined) {
              onToggleProduct(productId);
            }
          } else if (editTarget.kind === 'category') {
            onEditCategory(editTarget.entity.id, { nombre: patch.nombre });
          } else if (editTarget.kind === 'extra') {
            onEditExtra(editTarget.entity.id, { nombre: patch.nombre, precio: patch.precio });
          } else {
            onEditSection(editTarget.entity.id, { nombre: patch.nombre });
            // Las opciones del modificador son EXTRAS: sus ediciones (nombre/
            // precio) se aplican vía el mismo canal de patches de extras.
            if (patch.optionPatches) {
              for (const [extraId, optPatch] of Object.entries(patch.optionPatches)) {
                onEditExtra(extraId, optPatch);
              }
            }
          }
          closeSheet();
        }}
      />
    );
  };

  const renderReassignSheet = (): ReactElement | null => {
    if (!reassignOpen) return null;
    const selectedOrphanIds = orphans.filter((p) => selectedProductIds.has(p.id)).map((p) => p.id);
    const availableCategories = analysis.categories;

    return (
      <div
        data-testid="consolidated-review-reassign-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Reasignar productos a categoria"
        style={sheetBackdropStyle}
        onClick={() => setReassignOpen(false)}
      >
        <div style={sheetContainerStyle} onClick={(e) => e.stopPropagation()}>
          <div style={sheetHeaderStyle}>
            <div>
              <div style={sheetTitleStyle}>Asignar {selectedOrphanIds.length} producto(s) a categoria</div>
              <div style={sheetMetaStyle}>
                Las selecciones moveran estos productos desde &quot;Sin categoria&quot; a la categoria elegida.
              </div>
            </div>
            <button
              type="button"
              aria-label="Cerrar"
              style={sheetCloseButtonStyle}
              onClick={() => setReassignOpen(false)}
            >
              <IconX size={20} />
            </button>
          </div>
          <div>
            <label htmlFor="reassign-target" style={sheetFieldLabelStyle}>
              Categoria destino
            </label>
            <select
              id="reassign-target"
              data-testid="consolidated-review-reassign-select"
              value={reassignTargetId}
              onChange={(e) => setReassignTargetId(e.target.value)}
              style={sheetSelectStyle}
            >
              {availableCategories.length === 0 ? <option value="">No hay categorias disponibles</option> : null}
              {availableCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div style={sheetFooterStyle}>
            <button type="button" style={sheetGhostButtonStyle} onClick={() => setReassignOpen(false)}>
              Cancelar
            </button>
            <button
              type="button"
              data-testid="consolidated-review-reassign-submit"
              style={sheetPrimaryButtonStyle}
              disabled={!reassignTargetId || selectedOrphanIds.length === 0}
              onClick={() => {
                if (!reassignTargetId || selectedOrphanIds.length === 0) return;
                onReassignCategory(selectedOrphanIds, reassignTargetId);
                setReassignOpen(false);
              }}
            >
              Asignar
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ---------- Layout ----------

  const allSelected = totalSelectedProducts === totalProducts && totalProducts > 0;
  const someSelected = totalSelectedProducts > 0 && !allSelected;

  return (
    <div data-testid="consolidated-review" style={containerStyle}>
      <div data-testid="consolidated-review-toolbar" style={toolbarWrapperStyle}>
        <div style={toolbarRowStyle}>
          <CheckboxZone
            checked={allSelected}
            indeterminate={someSelected}
            ariaLabel={allSelected ? 'Excluir todos los productos' : 'Incluir todos los productos'}
            onToggle={toggleAllGlobal}
            testId="consolidated-review-bulk-toggle-all"
          />
          <span
            data-testid="consolidated-review-counter"
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: totalSelectedProducts === 0 && totalProducts > 0 ? V4.warningDark : V4.primary,
            }}
          >
            {totalSelectedProducts} de {totalProducts} incluido{totalSelectedProducts === 1 ? '' : 's'}
          </span>
        </div>
        <div data-testid="consolidated-review-hint" style={toolbarHintStyle}>
          Toca el cuadro para incluir o excluir · toca el nombre o el lápiz para editar
        </div>
      </div>

      <div style={headerStyle}>
        Detectamos {totalProducts} producto{totalProducts === 1 ? '' : 's'} en {totalCategories} categoria
        {totalCategories === 1 ? '' : 's'}
      </div>

      {analysis.warnings.wasTruncated ? (
        <div style={warningBannerStyle}>
          <IconAlertTriangle size={18} color={V4.warning} />
          <span>
            El analisis fue truncado por tamano. Algunos productos pueden no haberse capturado — revisa que no falte
            nada importante.
          </span>
        </div>
      ) : null}

      {analysis.warnings.suspiciouslyLowCount ? (
        <div style={warningBannerStyle}>
          <IconAlertTriangle size={18} color={V4.warning} />
          <span>Detectamos pocos productos respecto al tamano del menu. Verifica el resultado antes de continuar.</span>
        </div>
      ) : null}

      {/* DuplicateBanner se renderiza desde el padre (SmartMenuOnboarding) con su propio handler wireado. */}

      {renderOrphanBucket()}

      <div>{analysis.categories.map((category) => renderCategory(category))}</div>

      {analysis.extras.length > 0 ? (
        <>
          <div style={sectionTitleStyle}>Extras</div>
          <div>{analysis.extras.map((extra) => renderExtra(extra))}</div>
        </>
      ) : null}

      {analysis.sections.length > 0 ? (
        <>
          <div style={sectionTitleStyle}>Modificadores</div>
          <div data-testid="consolidated-review-sections" style={sectionGridStyle}>
            {analysis.sections.map((section) => renderSection(section))}
          </div>
        </>
      ) : null}

      {renderEditSheet()}
      {renderReassignSheet()}
    </div>
  );
};
